// Asset Detail page domain. The core page (preview, classification, attributes)
// reads REAL data already used by the Library grid (file-service via
// fileService.discover). Applicable rules are also real (GET
// /files/:id/applicable-rules). Version history and "Used in" remain MOCK
// stand-ins for capabilities with no backend yet — see
// mocks/seed/asset-detail-extras.ts and the corresponding MSW handlers.

import { groupTagsByTuple } from '@atlas/core-utils/classification'
import type { ObjectTag, RuleLifecycle, RuleSeverity, RuleType, RuleValidationMethod } from '@atlas/types'
import { AxisCode } from '@atlas/types'

/** A file's classification tags as a `Record<axis, string[]>`, using each tag's
 * already-resolved LABEL (not its value code) as the chip value — `ObjectTag`
 * carries a label, unlike Rules' raw `Selection` codes, so no separate lookup.
 *
 * This FLATTENS across tuples. Use it where a merged view is what's wanted (a
 * grid cell, a summary line); use {@link tagsToChipTuples} where the object's
 * separate assertions must stay separate. */
export function tagsToChipTuple(tags: ObjectTag[]): Record<string, string[]> {
	const tuple: Record<string, string[]> = {}
	for (const tag of tags) {
		;(tuple[tag.axisCode] ??= []).push(tag.label)
	}
	return tuple
}

/**
 * A file's tags grouped into its tuples, each as a chip-ready selection —
 * the shape `TupleChipSet` renders.
 *
 * Grouping happens before the axis→labels collapse, so a file asserting
 * `locale: ja-JP` in one tuple and `locale: en-US` in another keeps them in
 * separate groups instead of merging into one axis with two values, which would
 * read as "both at once".
 */
export function tagsToChipTuples(tags: ObjectTag[]): { tupleId: number; tuple: Record<string, string[]> }[] {
	return groupTagsByTuple(tags).map(({ tupleId, tags: tupleTags }) => ({
		tupleId,
		tuple: tagsToChipTuple(tupleTags),
	}))
}

/** The label of a file's single-value tag on an axis (e.g. licence, lifecycle), or
 * `null` when the file carries no tag on that axis — rendered as an explicit
 * "not yet tagged" placeholder, never a blank field. */
export function findAttributeLabel(tags: ObjectTag[], axisCode: AxisCode): string | null {
	return tags.find((t) => t.axisCode === axisCode)?.label ?? null
}

export const NOT_YET_TAGGED = 'Not yet tagged'

export { AxisCode }

/** A version-history row (MOCK — no `version`/`supersedes` field exists on IFile). */
export interface FileVersion {
	pin: string // e.g. "v3"
	note: string
	date: string // ISO date
}

/** A reverse reference from a work order or campaign (MOCK — no reverse-lookup endpoint exists). */
export interface FileUsageRef {
	id: string
	label: string
	kind: 'work-order' | 'campaign'
}

/** A rule matched against this file — real data from `GET /files/:id/applicable-rules`, shaped like the backend's `IRule` DTO (dates as ISO strings). */
export interface ApplicableRule {
	id: string
	tenantId: number
	statement: string
	type: RuleType
	severity: RuleSeverity
	checkDefinition: string
	validationMethod: RuleValidationMethod
	lifecycle: RuleLifecycle
	effectiveFrom: string
	effectiveTo: string | null
	isActive: boolean
}
